import { Avatar as MuiAvatar, type AvatarProps as MuiAvatarProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { type Theme } from '@dhl-ui/theme'
import { spacing } from '../../theme'
import type { DataTestIdAttribute } from '../../types'

export type AvatarProps = Pick<MuiAvatarProps, 'children' | 'alt' | 'src' | 'slotProps' | 'onError'> & {
  /**
   * Select your correct size, when other then medium
   *
   * @default 'medium'
   */
  size?: 'xs' | 's' | 'm' | 'l' | 'xl'

  /**
   * Choose blue when your designs say so :)
   *
   * @default 'default' (grey)
   */
  color?: 'default' | 'blue'
} & DataTestIdAttribute

const sizes: Record<'xs' | 's' | 'm' | 'l' | 'xl', string> = {
  xs: spacing(2),
  s: spacing(3),
  m: spacing(4),
  l: spacing(5),
  xl: spacing(20),
}

const fontSizes: Record<'xs' | 's' | 'm' | 'l' | 'xl', string> = {
  xs: spacing(0.75),
  s: spacing(1.25),
  m: spacing(1.5),
  l: spacing(2),
  xl: spacing(3),
}

const StyledAvatar = styled(MuiAvatar, {
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'color',
})<AvatarProps>(({ theme, color = 'default', size = 'm' }: { theme: Theme } & AvatarProps) => {
  const avatarSize = sizes[size]
  const fontSize = fontSizes[size]

  return {
    fontSize,
    width: avatarSize,
    height: avatarSize,
    backgroundColor: color === 'default' ? theme.palette.dark.main : theme.palette.components.avatar.backgroundBlue,
    color: color === 'default' ? theme.palette.dark.contrastText : theme.palette.static.light,
    svg: {
      width: '75%',
      height: '75%',
    },
  }
})

export const Avatar = ({ children, color = 'default', ...props }: AvatarProps) => (
  <StyledAvatar variant="circular" color={color} {...props}>
    {children}
  </StyledAvatar>
)
